import { useCallback, useEffect, useState } from 'react';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';

export const LIST_PAGE_SIZE = 10;

/** What every paginated procedure returns. */
type Page<TItem> = { items: Array<TItem>; nextCursor: string | null };

/** The `getNextPageParam` every cursor list passes to `infiniteQueryOptions`. */
export function nextCursorOf(page: { nextCursor: string | null }) {
  return page.nextCursor;
}

/**
 * Every page's items in order, each row once. Pages are fetched at different
 * moments, so a row that moved down between two fetches comes back on the next
 * page as well; the first copy is kept, and the next refetch of the whole list
 * puts it right. Without `keyOf` the rows are returned as they came.
 */
export function flattenPages<TItem>(
  pages: ReadonlyArray<Page<TItem>> | undefined,
  keyOf?: (item: TItem) => string,
): Array<TItem> {
  const items = pages?.flatMap((page) => page.items) ?? [];

  if (!keyOf) {
    return items;
  }

  const seen = new Set<string>();

  return items.filter((item) => {
    const key = keyOf(item);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

/**
 * The virtualised-pagination plumbing every list repeats: the status booleans
 * and the end-reached callback. A hook rather than a component because the
 * plumbing is identical while the rendering is not.
 *
 * ```ts
 * const query = useInfiniteQuery(
 *   trpc.a.b.list.infiniteQueryOptions(
 *     { limit: LIST_PAGE_SIZE },
 *     { getNextPageParam: nextCursorOf },
 *   ),
 * );
 * const list = usePaginatedList(query);
 * ```
 *
 * `onAtBottomChange` is offered, not imposed: wire it to Virtuoso and the list
 * keeps filling while the viewport is already at the end — which is the only
 * way a list shorter than its own viewport ever loads a second page. Leave it
 * unwired and nothing auto-loads.
 */
export function usePaginatedList<TItem>(
  query: Pick<
    UseInfiniteQueryResult<InfiniteData<Page<TItem>, unknown>, unknown>,
    | 'data'
    | 'fetchNextPage'
    | 'hasNextPage'
    | 'isFetchingNextPage'
    | 'isPending'
  >,
  /** A row's identity, for lists whose rows can move between pages. */
  keyOf?: (item: TItem) => string,
) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    query;
  const rows = flattenPages(data?.pages, keyOf);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const canLoadMore = hasNextPage && !isFetchingNextPage;
  const onEndReached = useCallback(() => {
    if (canLoadMore) {
      void fetchNextPage();
    }
  }, [canLoadMore, fetchNextPage]);

  useEffect(() => {
    if (isAtBottom && canLoadMore) {
      void fetchNextPage();
    }
  }, [isAtBottom, canLoadMore, fetchNextPage]);

  return {
    isEmpty: rows.length === 0 && !hasNextPage && !isPending,
    isLoading: isPending,
    isLoadingMore: isFetchingNextPage,
    onAtBottomChange: setIsAtBottom,
    onEndReached,
    rows,
  };
}
